import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import jp from 'jsonpath';
import { textToStatus } from '../health';
import { logger } from '../../utils';

const serviceURL = '/session/api/ext/monitoring/v1/query/execute';

export interface UQLFetch {
  name: string;
  attributeName?: string;
}

export interface UQLQueryAST {
  from?: {
    entities?: string[] | UQLEntity[];
  };
  fetch?: UQLFetch[];
}
export interface UQLEntity {
  name: string;
}

export interface ModelFields {
  alias: string;
  type: string;
  hints: {
    kind: string;
    field: string;
    type: string;
  };
  form?: string;
  model?: ModelDefinition;
}

export interface ModelDefinition {
  name: string;
  fields: ModelFields[];
}

export interface ModelChunk {
  type: 'model';
  model: ModelDefinition;
}

export type DataValues = unknown[];

export interface DataChunk {
  type: 'data';
  model: {
    $jsonPath: string;
    $model: string;
  };
  metadata: {
    since: string;
    until: string;
  };
  dataset: string;
  _links: {
    next: {
      href: string;
    };
  };
  data: DataValues;
}

export interface ErrorChunk {
  type: 'error';
}

export type UQLResponse = (ModelChunk | DataChunk | ErrorChunk)[];

type UQLFrom = {
  entities: string[];
};

type FetchCountType = {
  type: 'count';
};

interface QueryBuilderConfig {
  qstr: string;
  tr: string;
  from: UQLFrom;
  fetch?: UQLFetch[];
  entityFilter?: Record<string, unknown>[];
}

const addModelToIndex = (modelByName: { [x: string]: ModelDefinition }, model: ModelDefinition | undefined) => {
  if (model) {
    const { name, fields } = model;
    modelByName[name] = model;
    fields.forEach(field => {
      if (field.model) {
        addModelToIndex(modelByName, field.model);
      }
    });
  }
};

const dereference = (dataset: ModelChunk | DataChunk | ErrorChunk, resp: UQLResponse) => {
  let r;
  let model: { fields: { alias: string }[] };
  Object.keys(dataset).forEach(k => {
    if (k === 'model') {
      if ('model' in dataset) {
        model =
          'model' in dataset && '$jsonPath' in (dataset[k] as { $jsonPath: string })
            ? jp.query(resp, (dataset[k] as { $jsonPath: string }).$jsonPath)?.shift()
            : undefined;
      }
    }
    if (k === 'data') {
      if ('data' in dataset) {
        r = (dataset[k] as DataValues[]).map((datablock: DataValues) =>
          datablock.reduce((accum: Record<string, unknown>, d: unknown, i: number) => {
            const field = model?.fields[i]?.alias;

            if (d === Object(d) && Object.prototype.hasOwnProperty.call(d, '$jsonPath')) {
              const refDataset = jp.query(resp, (d as { $jsonPath: string }).$jsonPath)?.shift();
              accum[field as string] = dereference(refDataset, resp);
            } else {
              accum[field as string] = d;
            }
            return accum;
          }, {} as Record<string, unknown>),
        );
      }
    }
  });
  return r;
};

const transform = (resp: UQLResponse = []) => {
  const reducer = (
    accum: Record<string, unknown>,
    datablock: ModelChunk | DataChunk | ErrorChunk,
  ): Record<string, unknown> => {
    if (datablock.type === 'data' && datablock.dataset === 'd:main') {
      Object.keys(datablock).forEach(k => {
        if (k === 'model' && Object.prototype.hasOwnProperty.call(datablock[k], '$jsonPath')) {
          accum[k] = jp.query(resp, (datablock[k] as { $jsonPath: string }).$jsonPath);
        } else if (k === 'data') {
          accum['data'] = dereference(datablock as DataChunk, resp);
        } else {
          accum[k as keyof DataChunk] = datablock[k as keyof DataChunk];
        }
      });
    }
    return accum;
  };
  return resp.reduce(reducer, {});
};

export const executeQuery = async (query: string) => {
  const response = await axios.post(serviceURL, { query });
  const transformedResults = transform(response.data);
  logger.log('transformedResult', transformedResults);
  return transformedResults;
};

export const executeQueryPaginated = async (query: string, pageParam?: string) => {
  let response;
  if (pageParam) {
    response = await axios.get(`/session/api/ext${pageParam}`);
  } else {
    response = await axios.post(serviceURL, { query });
  }

  const transformedResults = transform(response.data);
  return transformedResults;
};

class QueryBuilder {
  private since: string = '-1h';
  private until: string = 'now';
  private queryStr: string = '';
  private queryAST: UQLQueryAST = {};
  private fetch: UQLFetch[] = [];
  private entityFilter: AttributeFilterBuilder | undefined;

  constructor({ qstr, from, fetch, entityFilter, tr }: QueryBuilderConfig) {
    if (qstr) {
      this.setQueryStr(qstr);
    } else {
    }

    if (from) {
      this.setFrom(from);
    }

    if (fetch) {
      this.setFetch(fetch);
    }

    if (tr) {
      this.setUiTimerange(tr);
    }

    if (entityFilter) {
      this.setEntityFilter(entityFilter);
    }
  }

  setEntityFilter(entityFilter: Record<string, unknown>[]) {
    this.entityFilter = new AttributeFilterBuilder(entityFilter);
  }

  setQueryStr(qstr: string) {
    this.queryStr = qstr;
    return this;
  }

  setFrom(fromObj: Record<string, unknown>) {
    this.queryAST.from = fromObj;
  }

  setFetch(fetchObj: UQLFetch[]) {
    this.queryAST.fetch = fetchObj;
  }

  setUiTimerange(since: string) {
    this.since = since;
    return this;
  }

  build() {
    const queryStr = [];

    if (this.since) {
      queryStr.push(`SINCE ${this.since}`);
    }

    if (this.until) {
      queryStr.push(`UNTIL ${this.until}`);
    }

    if (this.queryStr) {
      queryStr.push(this.queryStr);
    } else if (this.queryAST) {
      if (this.queryAST) {
        if (this.queryAST.from) {
          let e = '';
          if (Array.isArray(this.queryAST.from.entities) && this.queryAST.from.entities.length === 1) {
            e = this.queryAST.from.entities[0] as string;
          } else if (this.queryAST?.from?.entities) {
            e = `${this.queryAST.from.entities.join(', ')}`;
          }

          queryStr.push(`FROM entities(${e})`);
          if (this.entityFilter) {
            const filterStr = this.entityFilter.build();
            queryStr.push(filterStr);
          }
        }

        if (this.queryAST.fetch && Array.isArray(this.queryAST.fetch)) {
          const fields = this.queryAST.fetch.reduce<string[]>((accum, field) => {
            const name = field.name;
            switch (name) {
              case 'count':
                accum.push('count');
                break;
              case 'attributes':
                accum.push(`attributes(${field.attributeName})`);
                break;
            }
            return accum;
          }, []);
          queryStr.push(`FETCH ${fields.join(', ')}`);
        }
      }
    }
    return queryStr.join(' ');
    // throw new Error("Querybuilder build failed. QueryStr of QueryAST not defined")
  }
}

class AttributeFilterBuilder {
  private expressions: Record<string, unknown>[] = [];
  constructor(expressions: Record<string, unknown>[]) {
    expressions.map(e => {
      if (Array.isArray(e.value) && e.value.length > 0) {
        this.expressions.push(e);
      }
    });
  }

  _healthToStatus(health: string) {
    const statusCode = textToStatus(health);
    if (statusCode !== null) {
      return statusCode;
    }
  }

  build() {
    if (this.expressions.length === 0) {
      return '';
    }

    const expression: string[] = [];
    this.expressions.map(({ filterType, fieldType, field, operator, value }) => {
      if (!value) {
        return;
      }
      if (Array.isArray(value) && (value as any[]).length === 0) {
        return;
      }
      switch (filterType) {
        case 'health': {
          const s = [];
          s.push('attributes(status)');
          s.push(operator);
          if (typeof operator === 'string' && (operator as string).toLowerCase() == 'in' && Array.isArray(value)) {
            s.push(`[${(value as string[])?.map(v => this._healthToStatus(v)).join(',')}]`);
          } else {
            s.push(value);
          }
          expression.push(s.join(' '));
          break;
        }
        case 'text': {
          const s = [];
          s.push(`${fieldType}(${field})`);
          s.push(operator);
          if (Array.isArray(value)) {
            s.push(`[ ${(value as string[]).join(',')} ]`);
          } else {
            s.push(value);
          }
          expression.push(s.join(' '));
          break;
        }
      }
    });
    return `[ ${expression.join(' && ')} ]`;
  }
}

const useUQL = (queryBuilderConfig: QueryBuilderConfig) => {
  const qb = new QueryBuilder(queryBuilderConfig);

  const uql = qb.build();
  logger.log('uql', uql);

  return useQuery({
    queryKey: ['posts', uql],
    queryFn: () => executeQuery(uql),
  });
};

export { useUQL };
